import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Markdown } from "@tiptap/markdown";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import { CommentHighlight } from "./extensions/CommentHighlightMark";
import { Button } from "@/components/ui/button";
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Underline as UnderlineIcon,
  Highlighter,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Quote,
  Minus,
  Undo,
  Redo,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Table as TableIcon,
} from "lucide-react";

export interface TextSelection {
  start: number;
  end: number;
  text: string;
  originalText?: string;
  textChanged?: boolean;
}

export interface Comment {
  id: string;
  text: string;
  isGeneral: boolean;
  textSelections: TextSelection[];
  color?: string;
  number?: number;
}

interface PrettyEditorProps {
  content: string;
  onChange: (content: string) => void;
  onTextSelect?: (text: string, range: { start: number; end: number }) => void;
  onTextReplace?: (
    originalText: string,
    newText: string,
    position: number
  ) => void;
  comments?: Comment[];
  onHighlightClick?: (commentId: string) => void;
  testId?: string; // Optional test ID for E2E testing
}

export const PrettyEditor = ({
  content,
  onChange,
  onTextSelect,
  onTextReplace,
  comments = [],
  onHighlightClick,
  testId,
}: PrettyEditorProps) => {
  const showToolbarRef = useRef(false);
  const initialContentRef = useRef<string | null>(null);
  const isInitialLoadRef = useRef(false);
  const lastSelectionRef = useRef<{
    from: number;
    to: number;
    text: string;
  } | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const selectedTextRef = useRef<{
    text: string;
    range: { start: number; end: number };
  } | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Markdown, // Markdown extension includes underline support (≈text≈ syntax)
      Placeholder.configure({
        placeholder:
          "Start typing your analysis here, add comments, and track changes...",
      }),
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      Color,
      Superscript,
      Subscript,
      CommentHighlight,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[400px] p-6",
      },
    },
    onCreate: async ({ editor }) => {
      if (content) {
        isInitialLoadRef.current = true;
        // Let Tiptap's Markdown extension handle parsing - it knows about custom syntax like underline
        editor.commands.setContent(content);
        initialContentRef.current = content;
        isInitialLoadRef.current = false;
      }
    },
    onUpdate: ({ editor, transaction }) => {
      if (isInitialLoadRef.current) return;

      // Check if this is a text replacement
      if (lastSelectionRef.current && transaction.docChanged) {
        const { from, to } = editor.state.selection;
        const lastSel = lastSelectionRef.current;

        // If we had a selection and now we're typing, it's a replacement
        if (lastSel.from !== lastSel.to && from === to) {
          const newText = editor.state.doc.textBetween(lastSel.from, from);
          if (onTextReplace && lastSel.text !== newText) {
            onTextReplace(lastSel.text, newText, lastSel.from);
          }
        }
      }

      const markdown = editor.getMarkdown();
      onChange(markdown);
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to, empty } = editor.state.selection;
      console.log("[PrettyEditor] Selection update:", { from, to, empty });
      if (!empty) {
        const text = editor.state.doc.textBetween(from, to);
        if (text.trim()) {
          console.log("[PrettyEditor] Non-empty selection:", {
            text: text.substring(0, 50) + (text.length > 50 ? "..." : ""),
            from,
            to,
          });
          lastSelectionRef.current = { from, to, text };
          if (onTextSelect) {
            onTextSelect(text, { start: from, end: to });
          }
          showToolbarRef.current = true;
        } else {
          console.log("[PrettyEditor] Selection is whitespace only - clearing");
        }
      } else {
        console.log("[PrettyEditor] Selection cleared - notifying parent");
        lastSelectionRef.current = null;
        showToolbarRef.current = false;
        // Clear parent's selection state when text is unselected
        if (onTextSelect) {
          onTextSelect("", { start: 0, end: 0 });
        }
      }
    },
  });

  // Update editor content when prop changes
  useEffect(() => {
    if (!editor) return;

    const updateContent = async () => {
      // Only update if editor is not focused and content has changed
      if (!editor.isFocused) {
        try {
          const currentMarkdown = editor.getMarkdown();

          // Skip if content hasn't changed
          if (content === currentMarkdown) return;

          // Parse markdown to HTML when content changes
          if (content && content !== initialContentRef.current) {
            isInitialLoadRef.current = true;
            // Let Tiptap's Markdown extension handle parsing
            editor.commands.setContent(content, { contentType: "markdown" });
            initialContentRef.current = content;
            isInitialLoadRef.current = false;
          }
        } catch (error) {
          console.error("Error updating editor content:", error);
        }
      }
    };

    updateContent();
  }, [content, editor]);

  // Clear selection when the editor loses focus to avoid stale selection
  useEffect(() => {
    if (!editor) return;

    const handleBlur = () => {
      console.log("[PrettyEditor] Editor blur event - clearing selection");

      // Clear selection state immediately
      // No timeout needed because buttons use onMouseDown with preventDefault
      lastSelectionRef.current = null;
      selectedTextRef.current = null;
      setHasSelection(false);
      if (onTextSelect) {
        onTextSelect("", { start: 0, end: 0 });
      }
    };

    editor.on("blur", handleBlur);
    return () => {
      editor.off("blur", handleBlur);
    };
  }, [editor, onTextSelect]);

  // Apply comment highlights when comments change
  useEffect(() => {
    if (!editor || comments.length === 0) return;

    // Clear existing highlights first
    editor.commands.unsetCommentHighlight();

    // Apply new highlights
    comments
      .filter(
        (c) => !c.isGeneral && c.textSelections && c.textSelections.length > 0
      )
      .forEach((comment, idx) => {
        comment.textSelections.forEach((sel) => {
          if (sel.start !== undefined && sel.end !== undefined) {
            editor
              .chain()
              .setTextSelection({ from: sel.start, to: sel.end })
              .setCommentHighlight({
                commentId: comment.id,
                number: comment.number || idx + 1,
                color: comment.color || "hsl(var(--primary))",
                textChanged: sel.textChanged || false,
              })
              .run();
          }
        });
      });
  }, [comments, editor]);

  // Handle clicks on highlighted comments
  useEffect(() => {
    if (!editor || !onHighlightClick) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const highlightSpan = target.closest("[data-comment-id]");
      if (highlightSpan) {
        const commentId = highlightSpan.getAttribute("data-comment-id");
        if (commentId) {
          onHighlightClick(commentId);
        }
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener("click", handleClick);

    return () => {
      editorElement.removeEventListener("click", handleClick);
    };
  }, [editor, onHighlightClick]);

  if (!editor) {
    return null;
  }

  return (
    <div className="relative border rounded-lg bg-background shadow-sm h-full flex flex-col">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 flex flex-wrap gap-1 p-3 bg-muted/50 border-b backdrop-blur-sm flex-shrink-0">
        {/* Undo/Redo */}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <Redo className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Text Formatting */}
        <Button
          size="sm"
          variant={editor.isActive("bold") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleBold().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive("italic") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive("underline") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive("strike") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive("code") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleCode().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <Code className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive("highlight") ? "default" : "ghost"}
          onClick={() =>
            editor.chain().focus().toggleHighlight({ color: "#fef08a" }).run()
          }
          type="button"
          className="h-8 w-8 p-0"
        >
          <Highlighter className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Superscript/Subscript */}
        <Button
          size="sm"
          variant={editor.isActive("superscript") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <SuperscriptIcon className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive("subscript") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <SubscriptIcon className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Headings */}
        <Button
          size="sm"
          variant={
            editor.isActive("heading", { level: 1 }) ? "default" : "ghost"
          }
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          type="button"
          className="h-8 w-8 p-0"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={
            editor.isActive("heading", { level: 2 }) ? "default" : "ghost"
          }
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          type="button"
          className="h-8 w-8 p-0"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={
            editor.isActive("heading", { level: 3 }) ? "default" : "ghost"
          }
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          type="button"
          className="h-8 w-8 p-0"
        >
          <Heading3 className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Text Alignment */}
        <Button
          size="sm"
          variant={editor.isActive({ textAlign: "left" }) ? "default" : "ghost"}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={
            editor.isActive({ textAlign: "center" }) ? "default" : "ghost"
          }
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={
            editor.isActive({ textAlign: "right" }) ? "default" : "ghost"
          }
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <AlignRight className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={
            editor.isActive({ textAlign: "justify" }) ? "default" : "ghost"
          }
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <AlignJustify className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Lists */}
        <Button
          size="sm"
          variant={editor.isActive("bulletList") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive("orderedList") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Block Elements */}
        <Button
          size="sm"
          variant={editor.isActive("blockquote") ? "default" : "ghost"}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <Quote className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          type="button"
          className="h-8 w-8 p-0"
        >
          <Minus className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
          type="button"
          className="h-8 w-8 p-0"
        >
          <TableIcon className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto" data-testid={testId}>
        <EditorContent editor={editor} className="h-full" />
      </div>
    </div>
  );
};
